import { useId, useRef, useState } from 'react';
import { Paperclip, X } from 'lucide-react';
import { MERGE_FIELDS, mergeFieldsFor } from '@cloudsign/shared';
import type { MergeContext } from '@cloudsign/shared';
import { t } from '../../lib/i18n';
import { fillMergeFields, normaliseRichHtml } from './markup';
import { RichTextEditor } from './RichTextEditor';

export const MAX_ATTACHMENT_BYTES = 5 * 1024 * 1024;
const ALLOWED_TYPES = new Set([
  'application/pdf',
  'image/png',
  'image/jpeg',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
]);
const ALLOWED_EXT = /\.(pdf|png|jpe?g|docx|xlsx)$/i;

/** What the editor edits. `attachment` describes the stored file (the module's route does the upload). */
export interface EmailDraft {
  subject: string;
  /** Stored HTML subset (ADR 0020). */
  body: string;
  replyTo: string;
  attachment: { name: string; size: number } | null;
  qr: boolean;
  calendar: boolean;
}

/** Checks a chosen file. Returns a message, or null when it is acceptable. */
export function attachmentProblem(file: { name: string; size: number; type?: string }): string | null {
  if (file.size > MAX_ATTACHMENT_BYTES) {
    return t('p2w.email.attachmentTooBig', { name: file.name, size: (file.size / 1024 / 1024).toFixed(1), max: MAX_ATTACHMENT_BYTES / 1024 / 1024 });
  }
  if (!(file.type && ALLOWED_TYPES.has(file.type)) && !ALLOWED_EXT.test(file.name)) return t('p2w.email.attachmentType', { name: file.name });
  return null;
}

/** Sample value for each merge path, for previews. */
export function sampleValues(): Record<string, string> {
  return Object.fromEntries(MERGE_FIELDS.map((f) => [f.path, f.sample]));
}

export interface EmailEditorProps {
  value: EmailDraft;
  onChange: (next: EmailDraft) => void;
  /** Which merge fields to offer. */
  context: MergeContext;
  /** Called with a valid chosen file; the owning module uploads it, then sets `attachment`. */
  onAttachmentChosen?: (file: File) => void;
  /** Hide the switches a given email does not support. Both default to shown. */
  showQr?: boolean;
  showCalendar?: boolean;
  showReplyTo?: boolean;
  /** Offers "Reset to default" when given. */
  onReset?: () => void;
  errors?: { subject?: string; body?: string; replyTo?: string };
  disabled?: boolean;
}

/** Subject, body, reply-to, one attachment (max 5 MB), QR and calendar switches, and a preview with sample values. */
export function EmailEditor({
  value,
  onChange,
  context,
  onAttachmentChosen,
  showQr = true,
  showCalendar = true,
  showReplyTo = true,
  onReset,
  errors,
  disabled = false,
}: EmailEditorProps) {
  const id = useId();
  const picker = useRef<HTMLInputElement>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [preview, setPreview] = useState(false);
  const set = (patch: Partial<EmailDraft>) => onChange({ ...value, ...patch });
  const samples = sampleValues();
  const input =
    'block min-h-10 w-full rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-900 hover:border-slate-400 disabled:bg-slate-100 aria-[invalid=true]:border-red-600 sm:min-h-9';

  const pick = (file: File | undefined) => {
    if (!file) return;
    const problem = attachmentProblem(file);
    setFileError(problem);
    if (!problem) onAttachmentChosen?.(file);
    if (picker.current) picker.current.value = '';
  };

  const previewBody = fillMergeFields(normaliseRichHtml(value.body), samples);
  const previewSubject = fillMergeFields(value.subject, samples, { html: false });

  return (
    <div className="space-y-4">
      <div>
        <label htmlFor={`${id}-subject`} className="mb-1 block text-sm font-medium text-slate-800">
          {t('p2w.email.subject')}
        </label>
        <input
          id={`${id}-subject`}
          value={value.subject}
          disabled={disabled}
          maxLength={200}
          aria-invalid={errors?.subject ? true : undefined}
          aria-describedby={errors?.subject ? `${id}-subject-err` : undefined}
          onChange={(e) => set({ subject: e.target.value })}
          className={input}
        />
        {errors?.subject ? (
          <p id={`${id}-subject-err`} className="mt-1 text-sm text-red-700">
            {errors.subject}
          </p>
        ) : null}
      </div>

      <RichTextEditor label={t('p2w.email.body')} value={value.body} onChange={(body) => set({ body })} mergeFields={mergeFieldsFor(context)} error={errors?.body} disabled={disabled} />

      {showReplyTo ? (
        <div>
          <label htmlFor={`${id}-reply`} className="mb-1 block text-sm font-medium text-slate-800">
            {t('p2w.email.replyTo')}
          </label>
          <input
            id={`${id}-reply`}
            type="email"
            autoComplete="off"
            value={value.replyTo}
            disabled={disabled}
            aria-invalid={errors?.replyTo ? true : undefined}
            aria-describedby={`${id}-reply-hint${errors?.replyTo ? ` ${id}-reply-err` : ''}`}
            onChange={(e) => set({ replyTo: e.target.value })}
            className={input}
          />
          <p id={`${id}-reply-hint`} className="mt-1 text-xs text-slate-600">
            {t('p2w.email.replyToHint')}
          </p>
          {errors?.replyTo ? (
            <p id={`${id}-reply-err`} className="mt-1 text-sm text-red-700">
              {errors.replyTo}
            </p>
          ) : null}
        </div>
      ) : null}

      <div>
        <span id={`${id}-att`} className="mb-1 block text-sm font-medium text-slate-800">
          {t('p2w.email.attachment')}
        </span>
        {value.attachment ? (
          <p className="inline-flex items-center gap-2 rounded-md bg-slate-100 py-1 pl-3 pr-1 text-sm text-slate-800">
            <Paperclip aria-hidden="true" className="h-4 w-4" />
            <span>
              {value.attachment.name} ({Math.max(1, Math.round(value.attachment.size / 1024))} KB)
            </span>
            <button
              type="button"
              disabled={disabled}
              aria-label={t('p2w.email.attachmentRemove', { name: value.attachment.name })}
              onClick={() => set({ attachment: null })}
              className="inline-flex h-8 w-8 items-center justify-center rounded text-slate-600 hover:bg-slate-200 disabled:opacity-50"
            >
              <X aria-hidden="true" className="h-4 w-4" />
            </button>
          </p>
        ) : (
          <div>
            <input
              ref={picker}
              id={`${id}-file`}
              type="file"
              accept=".pdf,.png,.jpg,.jpeg,.docx,.xlsx"
              disabled={disabled}
              aria-labelledby={`${id}-att`}
              aria-describedby={`${id}-att-hint${fileError ? ` ${id}-att-err` : ''}`}
              onChange={(e) => pick(e.target.files?.[0])}
              className="block w-full text-sm text-slate-700 file:mr-3 file:min-h-10 file:rounded-md file:border file:border-slate-300 file:bg-white file:px-3.5 file:text-sm file:font-medium file:text-slate-800"
            />
          </div>
        )}
        <p id={`${id}-att-hint`} className="mt-1 text-xs text-slate-600">
          {t('p2w.email.attachmentHint', { max: MAX_ATTACHMENT_BYTES / 1024 / 1024 })}
        </p>
        {fileError ? (
          <p id={`${id}-att-err`} role="alert" className="mt-1 text-sm text-red-700">
            {fileError}
          </p>
        ) : null}
      </div>

      {showQr || showCalendar ? (
        <div className="space-y-2">
          {showQr ? (
            <label className="flex items-center gap-2 text-sm text-slate-800">
              <input type="checkbox" checked={value.qr} disabled={disabled} onChange={(e) => set({ qr: e.target.checked })} className="h-4 w-4" />
              {t('p2w.email.qr')}
            </label>
          ) : null}
          {showCalendar ? (
            <label className="flex items-center gap-2 text-sm text-slate-800">
              <input type="checkbox" checked={value.calendar} disabled={disabled} onChange={(e) => set({ calendar: e.target.checked })} className="h-4 w-4" />
              {t('p2w.email.calendar')}
            </label>
          ) : null}
        </div>
      ) : null}

      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          aria-expanded={preview}
          aria-controls={`${id}-preview`}
          onClick={() => setPreview((p) => !p)}
          className="inline-flex min-h-10 items-center rounded-md border border-slate-300 bg-white px-3.5 text-sm font-medium text-slate-800 hover:bg-slate-50 sm:min-h-9"
        >
          {t('p2w.email.preview')}
        </button>
        {onReset ? (
          <button
            type="button"
            disabled={disabled}
            onClick={onReset}
            className="inline-flex min-h-10 items-center rounded-md border border-transparent px-3.5 text-sm font-medium text-slate-700 hover:bg-slate-100 sm:min-h-9"
          >
            {t('p2w.email.reset')}
          </button>
        ) : null}
      </div>

      {preview ? (
        <section id={`${id}-preview`} aria-label={t('p2w.email.preview')} className="rounded-lg border border-slate-200 bg-slate-50 p-4">
          <p className="text-xs text-slate-600">{t('p2w.email.previewHint')}</p>
          <p className="mt-2 text-sm font-semibold text-slate-900">{t('p2w.email.previewSubject', { subject: previewSubject })}</p>
          {value.body.trim() === '' ? (
            <p className="mt-2 text-sm text-slate-600">{t('p2w.email.previewEmpty')}</p>
          ) : (
            // Safe: normaliseRichHtml rebuilds the HTML from the allowed subset and sample values are escaped.
            <div className="mt-2 space-y-2 text-sm text-slate-800 [&_a]:text-brand [&_a]:underline [&_blockquote]:border-l-4 [&_blockquote]:pl-3 [&_h2]:text-base [&_h2]:font-semibold [&_h3]:font-semibold [&_ol]:list-decimal [&_ol]:pl-5 [&_ul]:list-disc [&_ul]:pl-5" dangerouslySetInnerHTML={{ __html: previewBody }} />
          )}
          {value.attachment ? (
            <p className="mt-2 flex items-center gap-1 text-xs text-slate-700">
              <Paperclip aria-hidden="true" className="h-3.5 w-3.5" />
              {value.attachment.name}
            </p>
          ) : null}
          {value.qr ? <p className="mt-1 text-xs text-slate-700">{t('p2w.email.previewQr')}</p> : null}
          {value.calendar ? <p className="mt-1 text-xs text-slate-700">{t('p2w.email.previewCalendar')}</p> : null}
        </section>
      ) : null}
    </div>
  );
}
